import Icon from '@/components/snippets/icon/icon'
import clsx from 'clsx'
import { useState, useEffect } from 'preact/hooks'
import { globalState } from '@/components/helpers/utils'
import { generateSrcSet } from './ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { render } from 'preact'
import { hasB2BDiscount } from '@/components/custom-elements/b2b-price'

const IMAGE_WIDTHS = [320, 480, 560, 640, 720, 960]

// Utility function moved outside components
const getStockStatus = (variant, stockLevels) => {
  // If variant doesn't exist, return out-of-stock status
  if (!variant) return { inStock: false, text: '' }

  // Actual inventory quantity
  const quantity = variant.inventory_quantity

  // Check if there's a specific message for quantity 0
  const zeroStockLevel = stockLevels.find(stock => stock.max_quantity === 0)

  // If quantity is 0 and we have a specific message for it, use that message
  if (quantity <= 0 && zeroStockLevel) {
    return {
      inStock: false,
      text: zeroStockLevel.message,
      warning: true,
      stockLevel: quantity,
      textColour: zeroStockLevel.message_colour
    }
  }

  // If quantity is 0 but no specific message, use default out of stock message
  if (quantity <= 0) {
    return { inStock: false, text: 'Out of stock', warning: true, stockLevel: quantity, textColour: '#b3b3b3' }
  }

  // Check against thresholds (sorted from highest to lowest)
  for (const stock of stockLevels) {
    if (quantity <= stock.max_quantity) {
      return {
        inStock: true,
        text: stock.message,
        warning: true,
        stockLevel: quantity,
        textColour: stock.message_colour
      }
    }
  }

  // If no threshold is matched, item is fully in stock
  return {
    inStock: true,
    text: '',
    warning: false,
    stockLevel: quantity,
    text_color: ''
  }
}

// Modal Content Component - This will be rendered into the container
const ModalContent = ({ result, modalId, firstPromoline, secondPromoline, sizeGuide, onClose }) => {
  const [variant] = result.variants
  const [selectedSize, setSelectedSize] = useState('')
  const [loading, setLoading] = useState(false)

  const isB2BCustomer = window.globals?.customer?.b2b
  const b2bPricing = window.globals?.customer?.b2bPricing
  const productId = result.uid || result.id?.toString?.() || result.id
  const b2bData = isB2BCustomer && b2bPricing ? b2bPricing[productId] : null
  const hasQuickB2bCheck = isB2BCustomer && b2bData && b2bData.price
  const [b2bPriceCheck, setB2bPriceCheck] = useState(hasQuickB2bCheck)

  useEffect(() => {
    if (isB2BCustomer && variant?.id) {
      hasB2BDiscount(variant.id).then(hasDiscount => {
        setB2bPriceCheck(hasDiscount)
      }).catch(() => {
        setB2bPriceCheck(false)
      })
    } else {
      setB2bPriceCheck(false)
    }
  }, [isB2BCustomer, variant?.id])

  // For non-B2B customers, use regular pricing
  const finalPrice = variant.price
  const finalCompareAtPrice = variant.compare_at_price

  const handleAddToCart = async () => {
    const targetVariant = result.variants.find(v => v.option2 === selectedSize)
    if (!targetVariant) return

    if (window.Cart) {
      setLoading(true)
      try {
        await window.Cart.addLineItems({
          id: targetVariant.id,
          quantity: 1
        })
        setSelectedSize('')
        // Close modal after successful add to cart
        onClose()
      } catch (error) {
        console.error('Error adding item to cart:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <>
      {/* Modal Header */}
      <div className="shrink-0 flex items-center justify-between sticky top-0 h-[52px] md:h-[60px] pl-1 pr-0.25 md:pl-1.5 md:pr-0.625 border-b border-neutral-light z-20">
        <h2 className="h6 font-cnd font-bold uppercase">Add To Bag</h2>
        <button
          class="shrink-0 flex items-center justify-center size-[25px] link-fade"
          data-modal-parent={modalId}
          data-modal-role="close"
          aria-label="Close quick add modal"
          onClick={onClose}
        >
          <div class="size-[13px] children:size-full">
            <Icon id={'close'} />
          </div>
        </button>
      </div>
      {/* Modal Inner */}
      <div className="grow flex flex-col gap-y-1.5 overflow-y-scroll no-scrollbar overscroll-none children:shrink-0">
        {/* Product Images Carousel */}
        {result?.images?.length > 0 && (
          <div className="w-full overflow-hidden">
            <x-carousel
              className="flex w-full children:shrink-0"
              carousel-settings='{
                "draggable": true,
                "freeScroll": false,
                "wrapAround": false,
                "groupCells": false,
                "fade": false,
                "adaptiveHeight": false,
                "accessibility": true,
                "cellAlign": "left",
                "prevNextButtons": false,
                "pageDots": false
              }'
            >
              {result.images.map((image, index) => (
                <div key={index} className="w-3/4 max-w-[250px] aspect-1/1 mr-0.5 overflow-hidden">
                  <img
                    className="w-full h-full object-cover"
                    src={image.src}
                    srcSet={generateSrcSet(image.src, IMAGE_WIDTHS)}
                    sizes="50vw"
                    alt={image.alt || result.title || `Product image ${index + 1}`}
                  />
                </div>
              ))}
            </x-carousel>
          </div>
        )}
        {/* Product Details */}
        <div className="flex flex-col gap-y-1 px-1 md:px-1.5 pb-2.5">
          <h3 className="h5 font-cnd font-bold uppercase">{result.title}</h3>
          {isB2BCustomer ? (
            <b2b-price
              class="group/price flex gap-x-0.25"
              data-variant-id={result.variants[0].id}>
              <span data-original-price class="hidden group-data-[sale=true]/price:block line-through paragraph-b font-bold"></span>
              <span data-b2b-price class="group-data-[sale=true]/price:text-brand-red paragraph-b font-bold"></span>
            </b2b-price>
          ) : (
            <Price price={finalPrice} compareAtPrice={finalCompareAtPrice} className="flex gap-x-0.25" />
          )}
          {(b2bPriceCheck || firstPromoline || secondPromoline) && (
            <div className="flex flex-col gap-0.25">
              {b2bPriceCheck && (
                <Promoline promoline={{
                  title: 'Wholesale Price',
                  text_colour: '#9C0000',
                  background_colour: '#FCEBEB'
                }} promolineLocation="Quick Add" promolineSize="large" />
              )}
              {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="Quick Add" promolineSize="large" />}
              {!b2bPriceCheck && secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="Quick Add" promolineSize="large" />}
            </div>
          )}
          <hr className="border-neutral-medium" />
          {/* Size Selector */}
          <SizeSelector
            result={result}
            variants={result.variants}
            setSelectedSize={setSelectedSize}
            selectedSize={selectedSize}
            sizeGuide={sizeGuide}
          />
        </div>
      </div>
      {/* Action Buttons */}
      <div className="shrink-0 flex flex-col gap-y-0.5 px-1 md:px-1.5 py-1.25 md:py-1.5 bg-neutral-light">
        <button
          className={clsx(
            'shrink-0 btn btn-primary btn-m w-full',
            !selectedSize ? 'btn-border disabled' : 'btn-fill',
            loading && ' disabled'
          )}
          type="button"
          onClick={() => {
            if (selectedSize) {
              handleAddToCart()
            }
          }}
          aria-label="Add To Bag"
        >
          <div className="w-[13px] h-[14px] children:size-full">
            <Icon id={'bag'} />
          </div>
          <span>{!selectedSize ? 'Select Size' : 'Add To Bag'}</span>
        </button>
        <a href={result.url} className="btn btn-primary btn-text btn-m w-full">
          <span className="font-line">View Product Details</span>
        </a>
      </div>
    </>
  )
}

const SizeSelector = ({ result, variants, setSelectedSize, selectedSize, sizeGuide }) => {
  const stockLevels = globalState?.metaobjects.stock_indicators?.sort((a, b) => b.max_quantity - a.max_quantity) || []

  const getStatusForSize = (size) => {
    const variant = variants.find(v => v.option2 === size)
    return getStockStatus(variant, stockLevels)
  }

  const handleSizeGuideClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (sizeGuide?.id) {
      const modalId = `size-modal-${sizeGuide.id}`
      const modalElement = document.querySelector(`x-modal[data-id="${modalId}"]`)
      if (modalElement) {
        const openButton = modalElement.querySelector('[data-modal-role="open"]')
        if (openButton) {
          openButton.click()
        } else {
          const modalInstance = modalElement
          if (modalInstance && typeof modalInstance.handleShow === 'function') {
            modalInstance.handleShow()
          }
        }
      }
    }
  }

  return (
    <div className="w-full">
      <div className="flex flex-col">
        <div className="flex justify-between gap-x-1 relative">
          <span className="text-b mb-0.25 whitespace-nowrap text-ellipsis overflow-hidden">
            <span className="font-bold">{result.is_giftcard !== 'true' ? `Size${result.tags?.includes('SubCat-Footwear') ? (window.globals?.schema?.size_append_text ? ' ' + window.globals.schema.size_append_text : '') : ''}` : 'Value'}:</span> Select
          </span>
          {(sizeGuide && sizeGuide.tables.length > 0) && (
            <button
              type="button"
              className="absolute inset-tr w-fit font-ext text-xxs tracking-1 border-primary font-bold uppercase font-line link-fade bg-secondary z-2"
              onClick={handleSizeGuideClick}
              aria-label="View Size Guide"
            >
              Size Guide
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-x-0.5 gap-y-0.25 relative">
          {variants.map((variant) => {
            const status = getStatusForSize(variant.option2)
            return (
              <div
                key={variant.id}
                role="option"
                aria-selected={selectedSize === variant.option2}
                tabIndex={status.inStock ? 0 : -1}
                className={`
                  group/option grow w-full max-w-grid-1/4 md:max-w-grid-1/5
                  ${selectedSize === variant.option2 ? 'active' : ''}
                `}
                onClick={() => {
                  if (status.inStock) {
                    setSelectedSize(variant.option2)
                  }
                }}
                aria-disabled={!status.inStock}
              >
                <div className={`btn btn-m ${status.inStock ? 'btn-border btn-primary-alt' : 'btn-fill btn-light btn-light-alt'} w-full !px-0.25 group-[.active]/option:border-primary`}>
                  <span className="text-xs font-bold uppercase overflow-hidden text-ellipsis whitespace-nowrap">{variant.option2}</span>
                </div>
                <span className="absolute -top-0.25 left-0 w-full max-w-variant-title font-sans text-b text-primary bg-secondary opacity-0 text-left group-[.active]/option:opacity-100 group-hover/option:opacity-100 whitespace-nowrap -translate-y-full overflow-hidden text-ellipsis pointer-events-none group-hover/option:z-2 transition-all">
                  <span className="font-bold">
                    {result.is_giftcard !== 'true' ? `Size${result.tags?.includes('SubCat-Footwear') ? (window.globals?.schema?.size_append_text ? ' ' + window.globals.schema.size_append_text : '') : ''}` : 'Value'}:</span> {variant.option2}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function ResultQuickAdd({ result, firstPromoline, secondPromoline, sizeGuide }) {
  const buttonClass = 'btn btn-primary btn-transparent lg:btn-fill lg-max:btn-square lg:btn-s w-[20px] lg:w-full lg-max:!px-0 lg:opacity-0 group-hover/card:opacity-100'

  const hasSingleVariant = result.variants.length === 1
  const singleVariant = hasSingleVariant ? result.variants[0] : null

  const handleAddToCart = async (variantToAdd = null) => {
    if (!variantToAdd) return

    if (window.Cart) {
      try {
        await window.Cart.addLineItems({
          id: variantToAdd.id,
          quantity: 1
        })
      } catch (error) {
        console.error('Error adding item to cart:', error)
      }
    }
  }

  const closeModal = (modalId) => {
    const modal = document.querySelector(`x-modal[data-id="${modalId}"]`)
    if (modal && typeof modal.handleHide === 'function') {
      modal.handleHide()
    }
  }

  const openModal = (modalId) => {
    setTimeout(() => {
      const modal = document.querySelector(`x-modal[data-id="${modalId}"]`)
      if (modal && typeof modal.handleToggle === 'function') {
        modal.handleToggle()
      }
    }, 100)
  }

  const handleModalOpen = () => {
    const modalId = 'quick-add-modal'
    const quickAddContainer = document.getElementById('quick-add-modal-inner')
    if (quickAddContainer) {
      render(
        <ModalContent
          result={result}
          firstPromoline={firstPromoline}
          secondPromoline={secondPromoline}
          sizeGuide={sizeGuide}
          onClose={() => closeModal(modalId)}
        />,
        quickAddContainer
      )
    }
    openModal(modalId)
  }

  // For single variant products, render a button that adds directly to cart
  if (hasSingleVariant) {
    const stockLevels = globalState?.metaobjects.stock_indicators?.sort((a, b) => b.max_quantity - a.max_quantity) || []
    const stockStatus = getStockStatus(singleVariant, stockLevels)
    const isDisabled = !stockStatus.inStock

    return (
      <button
        className={clsx(
          buttonClass,
          isDisabled && 'disabled'
        )}
        type="button"
        onClick={() => handleAddToCart(singleVariant)}
        disabled={isDisabled}
        aria-label="Add to cart"
      >
        <div className="w-[16px] lg:w-[13px] h-[18px] lg:h-[14px] children:size-full">
          <Icon id={'bag'} />
        </div>
        <span className="lg-max:hidden">
          Quick Add
        </span>
      </button>
    )
  }

  // For multiple variant products, render a button that opens quick add modal with additional info
  return (
    <button
      className={buttonClass}
      type="button"
      onClick={handleModalOpen}
      aria-label="Open quick add modal"
    >
      <div className="w-[16px] lg:w-[13px] h-[18px] lg:h-[14px] children:size-full">
        <Icon id={'bag'} />
      </div>
      <span className="lg-max:hidden">
        Quick Add
      </span>
    </button>
  )
}
